import { Injectable } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap, finalize, catchError, take } from 'rxjs/operators';
import { Location } from '@angular/common';
import { CognitoServiceService } from './cognito-service.service';
import { QuicksightService } from './user/quicksight.service';
import { TokenPropertyService } from './token-property.service';
import { TilesService } from './user/tiles.service';
import { CloudWatchRumService } from './cloudwatch-rum.service';
import { SESSION_STORAGE_KEYS } from '../../constants';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class TokenResolverService {

  constructor(
    private location: Location,
    private awsCognitoService: CognitoServiceService,
    private quicksightService: QuicksightService,
    private tokenPropertyService: TokenPropertyService,
    private router: Router,
    private tilesService: TilesService,
    private cloudWatchRumService: CloudWatchRumService
  ) {}

  resolve: ResolveFn<any> = () => {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const code: string | null = urlParams.get('code');

    if (!code) {
      return of(null);
    }

    return this.getTokenDetailsFromCognito(code).pipe(
      finalize(() => {
        // Remove query string and trigger page refresh
        window.location.href = window.location.pathname;
      })
    );
  }

  getTokenDetailsFromCognito(code: string): Observable<any | null> {
    return this.awsCognitoService.getTokenDetailsFromCognito(code).pipe(
      switchMap(async (response: any) => {
        if (!response?.access_token) {
          this.router.navigate(['/error']);
          return response;
        }

        // Set tokens in localStorage
        localStorage.setItem('token', response.access_token);
        localStorage.setItem('id_token', response.id_token);
        localStorage.setItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY, 'false');
        localStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_HISTORY);
        localStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_SESSION);
        
        // Clear embed URL cache for new session
        this.tilesService.clearEmbedUrlCache();
        
        // Notify TokenPropertyService that tokens have been updated
        this.tokenPropertyService.updateTokenState();

        // Record login event in CloudWatch RUM
        this.cloudWatchRumService.recordLoginEvent();

        // Check if QuickSight registration is needed
        const isRegistered = localStorage.getItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY) === 'true';
        
        if (!isRegistered) {
          try {
            // Wait for QuickSight registration to complete
            await firstValueFrom(this.quicksightService.checkQuicksightAccess());
            localStorage.setItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY, 'true');
          } catch (error) {
            console.error('QuickSight registration failed:', error);
            // Don't block the flow on registration failure
          }
        }

        // Continue with admin access check and navigation
        const adminAccess = await firstValueFrom(
          this.tokenPropertyService.propertyHasValue('custom:ADgroup', 'true')
        );

        const isInternal = localStorage.getItem('isInternal') === 'true';
        localStorage.removeItem('isInternal');

        if (!isInternal) {
          this.router.navigate(['/home/recommendations']);
        } else if (isInternal && adminAccess) {
          this.router.navigate(['/admin']);
        } else {
          this.router.navigate(['/error']);
        }

        return response;
      }),
      catchError((error) => {
        console.error('Error in token service:', error);
        return of(error);
      })
    );
  }
}